import { Button, Switch } from "@heroui/react";
import { Plus, X } from "lucide-react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { TextInputField } from "@/components/kit";
import { SupplierFormData, WEEKDAYS } from "@/schemas/supplier-form.schema";

const DEFAULT_WINDOW = { open: "08:00", close: "22:00" };

const DayHours = ({ dayIndex, label }: { dayIndex: number; label: string }) => {
  const form = useFormContext<SupplierFormData>();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: `days.${dayIndex}.windows`,
  });

  return (
    <li className="flex items-start gap-3 py-3">
      <span className="w-24 shrink-0 pt-2 text-sm font-medium">{label}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {fields.length === 0 ? (
          <span className="text-muted pt-2 text-sm">Closed</span>
        ) : (
          fields.map((field, index) => (
            <div key={field.id} className="flex items-start gap-2">
              <TextInputField<SupplierFormData>
                label="Opens"
                name={`days.${dayIndex}.windows.${index}.open`}
                type="time"
              />
              <TextInputField<SupplierFormData>
                label="Closes"
                name={`days.${dayIndex}.windows.${index}.close`}
                type="time"
              />
              <Button
                isIconOnly
                aria-label={`Remove ${label} hours`}
                className="mt-6"
                size="sm"
                variant="ghost"
                onPress={() => remove(index)}
              >
                <X className="size-4" />
              </Button>
            </div>
          ))
        )}
      </div>
      <Button
        isIconOnly
        aria-label={`Add ${label} hours`}
        size="sm"
        variant="ghost"
        onPress={() => append(DEFAULT_WINDOW)}
      >
        <Plus className="size-4" />
      </Button>
    </li>
  );
};

export const SupplierOpeningHours = () => {
  const form = useFormContext<SupplierFormData>();
  const alwaysOpen = useWatch({ control: form.control, name: "alwaysOpen" });

  return (
    <div className="flex flex-col gap-3">
      <Controller
        control={form.control}
        name="alwaysOpen"
        render={({ field }) => (
          <Switch
            isSelected={field.value}
            onBlur={field.onBlur}
            onChange={field.onChange}
          >
            <Switch.Content>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <span className="flex flex-col">
                <span className="text-sm font-medium">Always open</span>
                <span className="text-muted text-xs">
                  Turn off to set weekly hours, Dubai time
                </span>
              </span>
            </Switch.Content>
          </Switch>
        )}
      />
      {alwaysOpen ? null : (
        <>
          <ul className="divide-separator divide-y">
            {WEEKDAYS.map(({ day, label }) => (
              <DayHours key={day} dayIndex={day} label={label} />
            ))}
          </ul>
          <p className="text-muted text-xs">
            A day without hours is closed. A 00:00 close means midnight.
          </p>
        </>
      )}
    </div>
  );
};
